import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Trash2, Plus, IndianRupee, Target as TargetIcon, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const METRICS = ['leads', 'qualified', 'visits', 'closures', 'revenue'];
const PERIOD_TYPES = ['monthly', 'weekly', 'daily'];
const SOURCES = ['Meta', 'Google', 'Website', 'Referral', 'Cold Call', 'Other'];

const AdminEntryPanel = ({ api, onChanged }) => {
  return (
    <Tabs defaultValue="targets" className="w-full" data-testid="admin-entry-panel">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="targets" data-testid="entry-tab-targets"><TargetIcon className="h-3.5 w-3.5 mr-1" /> Targets</TabsTrigger>
        <TabsTrigger value="spend" data-testid="entry-tab-spend"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Marketing Spend</TabsTrigger>
        <TabsTrigger value="payroll" data-testid="entry-tab-payroll"><Wallet className="h-3.5 w-3.5 mr-1" /> Payroll</TabsTrigger>
      </TabsList>

      <TabsContent value="targets" className="mt-4"><TargetsTab api={api} onChanged={onChanged} /></TabsContent>
      <TabsContent value="spend"   className="mt-4"><SpendTab   api={api} onChanged={onChanged} /></TabsContent>
      <TabsContent value="payroll" className="mt-4"><PayrollTab api={api} onChanged={onChanged} /></TabsContent>
    </Tabs>
  );
};

// ====================== TARGETS ======================
const TargetsTab = ({ api, onChanged }) => {
  const [list, setList] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [form, setForm] = useState({
    executiveId: '', executiveName: '', team: 'Servall', periodType: 'monthly',
    periodStart: '', periodEnd: '', metric: 'leads', targetValue: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [t, u] = await Promise.all([
        api.get('/reports/targets'),
        api.get('/users')
      ]);
      setList(t.data);
      setExecutives((u.data || []).filter(x => ['Executive', 'Manager'].includes(x.role)));
    } catch (e) { toast.error('Failed to load targets'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submit = async () => {
    if (!form.periodStart || !form.periodEnd || !form.targetValue) {
      toast.error('Period and target value are required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form, targetValue: parseFloat(form.targetValue),
        executiveId: form.executiveId || null,
        executiveName: form.executiveName || null,
      };
      await api.post('/reports/targets', payload);
      toast.success('Target saved');
      setForm({ ...form, targetValue: '', notes: '' });
      await fetchAll();
      if (onChanged) onChanged();
    } catch (e) { toast.error('Failed to save target'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try { await api.delete(`/reports/targets/${id}`); await fetchAll(); if (onChanged) onChanged(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Set Sales Targets</CardTitle>
        <CardDescription>Per-executive or team-wide targets. Used in Target vs Achievement report.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Executive (optional)</Label>
            <Select value={form.executiveId || 'team'} onValueChange={(v) => {
              if (v === 'team') setForm({ ...form, executiveId: '', executiveName: '' });
              else { const ex = executives.find(e => e.id === v); setForm({ ...form, executiveId: v, executiveName: ex?.name || '' }); }
            }}>
              <SelectTrigger data-testid="target-exec-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="team">Team-wide</SelectItem>
                {executives.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Metric</Label>
            <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
              <SelectTrigger data-testid="target-metric"><SelectValue /></SelectTrigger>
              <SelectContent>{METRICS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Period Type</Label>
            <Select value={form.periodType} onValueChange={(v) => setForm({ ...form, periodType: v })}>
              <SelectTrigger data-testid="target-period-type"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIOD_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Target Value</Label>
            <Input type="number" min="0" value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: e.target.value })} data-testid="target-value-input" />
          </div>
          <div>
            <Label className="text-xs">Period Start</Label>
            <Input type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} data-testid="target-period-start" />
          </div>
          <div>
            <Label className="text-xs">Period End</Label>
            <Input type="date" value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} data-testid="target-period-end" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" data-testid="target-notes" />
          </div>
        </div>
        <Button onClick={submit} disabled={saving} data-testid="target-save-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Target
        </Button>

        <div className="border-t pt-3">
          <p className="text-sm font-medium mb-2">Existing targets ({list.length})</p>
          {list.length === 0 ? <p className="text-xs text-muted-foreground">No targets yet.</p> : (
            <div className="space-y-1 max-h-72 overflow-auto" data-testid="target-list">
              {list.map(t => (
                <div key={t.id} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{t.executiveName || 'Team'}</span>
                    <span className="text-muted-foreground"> · {t.metric} · {t.targetValue}</span>
                    <span className="text-xs text-muted-foreground"> ({t.periodStart} → {t.periodEnd})</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(t.id)} data-testid={`target-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ====================== MARKETING SPEND ======================
const SpendTab = ({ api, onChanged }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ source: 'Meta', spendDate: '', amount: '', campaignName: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try { const r = await api.get('/reports/marketing-spend'); setList(r.data); }
    catch { toast.error('Failed to load spend'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submit = async () => {
    if (!form.spendDate || !form.amount) { toast.error('Date and amount required'); return; }
    setSaving(true);
    try {
      await api.post('/reports/marketing-spend', { ...form, amount: parseFloat(form.amount) });
      toast.success('Spend saved');
      setForm({ ...form, amount: '', campaignName: '', notes: '' });
      await fetchAll(); if (onChanged) onChanged();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this spend entry?')) return;
    try { await api.delete(`/reports/marketing-spend/${id}`); await fetchAll(); if (onChanged) onChanged(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const total = list.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Log Marketing Spend</CardTitle>
        <CardDescription>Per source / campaign. Drives Total Marketing Spend and CPA reports. Lifetime total: <strong>₹{total.toLocaleString('en-IN')}</strong></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="spend-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.spendDate} onChange={(e) => setForm({ ...form, spendDate: e.target.value })} data-testid="spend-date" />
          </div>
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="spend-amount" />
          </div>
          <div>
            <Label className="text-xs">Campaign</Label>
            <Input value={form.campaignName} onChange={(e) => setForm({ ...form, campaignName: e.target.value })} placeholder="Optional" data-testid="spend-campaign" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" data-testid="spend-notes" />
          </div>
        </div>
        <Button onClick={submit} disabled={saving} data-testid="spend-save-btn"><Plus className="h-4 w-4 mr-1" /> Add Spend</Button>

        <div className="border-t pt-3">
          <p className="text-sm font-medium mb-2">Recent entries ({list.length})</p>
          {list.length === 0 ? <p className="text-xs text-muted-foreground">No spend entries yet.</p> : (
            <div className="space-y-1 max-h-72 overflow-auto" data-testid="spend-list">
              {list.map(s => (
                <div key={s.id} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{s.source}</span>
                    <span className="text-muted-foreground"> · ₹{(s.amount || 0).toLocaleString('en-IN')} · {s.spendDate}</span>
                    {s.campaignName && <span className="text-xs text-muted-foreground"> · {s.campaignName}</span>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`spend-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ====================== PAYROLL ======================
const PayrollTab = ({ api, onChanged }) => {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ userId: '', userName: '', month: '', baseSalary: '', incentive: '', otherCosts: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, u] = await Promise.all([api.get('/reports/payroll'), api.get('/users')]);
      setList(p.data);
      setUsers((u.data || []).filter(x => ['Executive', 'Manager', 'CEO', 'Super Admin'].includes(x.role)));
    } catch { toast.error('Failed to load payroll'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submit = async () => {
    if (!form.userId || !form.month) { toast.error('User and month required'); return; }
    setSaving(true);
    try {
      await api.post('/reports/payroll', {
        userId: form.userId, userName: form.userName, month: form.month,
        baseSalary: parseFloat(form.baseSalary || 0),
        incentive: parseFloat(form.incentive || 0),
        otherCosts: parseFloat(form.otherCosts || 0),
      });
      toast.success('Payroll saved');
      setForm({ ...form, baseSalary: '', incentive: '', otherCosts: '' });
      await fetchAll(); if (onChanged) onChanged();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this payroll entry?')) return;
    try { await api.delete(`/reports/payroll/${id}`); await fetchAll(); if (onChanged) onChanged(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Log Sales Payroll Cost</CardTitle>
        <CardDescription>Per user, per month. Drives Sales Payroll Cost &amp; CPA. Re-entering same (User, Month) updates the value.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">User</Label>
            <Select value={form.userId} onValueChange={(v) => { const u = users.find(x => x.id === v); setForm({ ...form, userId: v, userName: u?.name || '' }); }}>
              <SelectTrigger data-testid="payroll-user"><SelectValue placeholder="Select user" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Month (YYYY-MM)</Label>
            <Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="payroll-month" />
          </div>
          <div>
            <Label className="text-xs">Base Salary</Label>
            <Input type="number" min="0" value={form.baseSalary} onChange={(e) => setForm({ ...form, baseSalary: e.target.value })} data-testid="payroll-base" />
          </div>
          <div>
            <Label className="text-xs">Incentive</Label>
            <Input type="number" min="0" value={form.incentive} onChange={(e) => setForm({ ...form, incentive: e.target.value })} data-testid="payroll-incentive" />
          </div>
          <div>
            <Label className="text-xs">Other Costs</Label>
            <Input type="number" min="0" value={form.otherCosts} onChange={(e) => setForm({ ...form, otherCosts: e.target.value })} data-testid="payroll-other" />
          </div>
        </div>
        <Button onClick={submit} disabled={saving} data-testid="payroll-save-btn"><Plus className="h-4 w-4 mr-1" /> Save Payroll</Button>

        <div className="border-t pt-3">
          <p className="text-sm font-medium mb-2">Entries ({list.length})</p>
          {list.length === 0 ? <p className="text-xs text-muted-foreground">No payroll entries yet.</p> : (
            <div className="space-y-1 max-h-72 overflow-auto" data-testid="payroll-list">
              {list.map(p => (
                <div key={p.id} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{p.userName}</span>
                    <span className="text-muted-foreground"> · {p.month} · Total ₹{(p.total || 0).toLocaleString('en-IN')}</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">Base ₹{p.baseSalary} · Inc ₹{p.incentive} · Other ₹{p.otherCosts}</Badge>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`payroll-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminEntryPanel;
